import React, { useRef, useEffect, useState } from "react";

import {
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  Pagination,
  CardHeader,
  Divider,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery
} from "@mui/material";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from "recharts";

import "../styles/chart.css";
import noDataImg from "../assets/no-data.png";

export default function TemperatureChart({ rawData, unit, date }) {

  const chartRef = useRef();
  const scrollRef = useRef();

  // ✅ Responsive detection
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const VALUES_PER_PAGE =
    isMobile ? 3 :
    isTablet ? 5 :
    9;

  const ITEM_WIDTH =
    isMobile ? 90 :
    isTablet ? 100 :
    120;


  // ✅ Pagination state
  const [page, setPage] = useState(1);


  // ✅ Temperature convert
  const convertTemp = (c) => {
    if (unit === "F") return c * 9/5 + 32;
    if (unit === "K") return c + 273.15;
    return c;
  };


  // ✅ Prepare chart data
  const temperatureData = [...rawData].reverse().map(d => ({
    time: d.time,
    temperature: parseFloat(convertTemp(d.temperature).toFixed(1))
  }));


  // ✅ Latest entry
  const latestTemp = temperatureData.length ? temperatureData[0] : null;


  // ✅ Total pages
  const totalPages = Math.ceil(
    temperatureData.length / VALUES_PER_PAGE
  );

  const chartWidth = Math.max(
  temperatureData.length * ITEM_WIDTH,
  VALUES_PER_PAGE * ITEM_WIDTH
);

  // ✅ Scroll when page changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: (page - 1) * VALUES_PER_PAGE * ITEM_WIDTH,
        behavior: "smooth"
      });
    }
  }, [page, unit, VALUES_PER_PAGE, ITEM_WIDTH]);


  // ✅ Reset when unit or screen size changes
  useEffect(() => {
    if(scrollRef.current)
      scrollRef.current.scrollLeft = 0;
    setPage(1);
  }, [unit, VALUES_PER_PAGE]);

if (!rawData || rawData.length === 0) {
  return (
    <Card elevation={0} sx={{
            boxShadow: "none",
            backgroundImage: "none"
          }}>
      <CardContent
          sx={{
            height: 350,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundImage: `url(${noDataImg})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundColor: "transparent",
            "&::before": { display: "none" }
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.7)"
            }}
          />

          <Box sx={{ position: "relative" }}>
            <Typography variant="h6">No Data Available</Typography>
            <Typography variant="body2">
              Start recording temperature to see insights
            </Typography>
          </Box>
        </CardContent>
    </Card>
  );
}
else{
return (
    <Container maxWidth="lg">
      <Grid container>
        <Grid item xs={12}>
          <Card>
            {/* Header */}
            <CardHeader
              avatar={
                <Avatar
                  src="/logo.svg"
                  sx={{
                    bgcolor: "transparent",
                    width: 40,
                    height: 40
                  }}/>
              }
              title="Temperature Report"
              subheader={date}/>
            <Divider />

            {/* Top info cards */}
            <Grid
              container
              spacing={2}
              justifyContent="center"
              sx={{ mt: 2, px: 2 }}>

              <Grid item xs={6} sm={4} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="body2">
                      Latest Temperature
                    </Typography>

                    <Typography variant="h4">
                      {latestTemp ? latestTemp.temperature : "--"} °{unit}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="body2">
                      Latest Time
                    </Typography>
                    <Typography variant="h4">
                      {latestTemp ? latestTemp.time : "--"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Chart */}
            <CardContent>
              <div ref={scrollRef} className="scroll">
                <div
                  ref={chartRef}
                  style={{
                    minWidth: chartWidth
                  }}>

                  <AreaChart
                    width={chartWidth}
                    height={isMobile ? 250 : 400}
                    data={temperatureData}>

                    {/* Gradient */}
                    <defs>
                      <linearGradient id="lineGradient">
                        <stop offset="0%" stopColor="#ff9800"/>
                        <stop offset="50%" stopColor="#ffeb3b"/>
                        <stop offset="100%" stopColor="#2196f3"/>
                      </linearGradient>

                      <linearGradient id="fillGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff9800" stopOpacity={0.5}/>
                        <stop offset="100%" stopColor="#2196f3" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3"/>

                    <XAxis dataKey="time"/>

                    <YAxis
                      domain={[
                        (dataMin) => dataMin - 0.5,
                        (dataMax) => dataMax + 0.5
                      ]}/>


                    <Tooltip formatter={(v) => `${v} °${unit}`}/>

                    <Area
                      type="natural"
                      dataKey="temperature"
                      stroke="url(#lineGradient)"
                      fill="url(#fillGradient)"
                      strokeWidth={4}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                      animationDuration={800}/>
                  </AreaChart>
                </div>
              </div>

              {/* Pagination */}
              <Box
                display="flex"
                justifyContent="center"
                mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  size={isMobile ? "medium" : "large"}
                  color="primary"/>
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
}